import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ResponsiveContainer, AreaChart, Area, YAxis, XAxis, Tooltip, CartesianGrid } from 'recharts';
import portfolioService, { Portfolio, PortfolioHoldingsBreakdown } from '../services/portfolio';
import authService from '../services/auth';
import api from '../services/api';
import useAuth from '../hooks/useAuth';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { usePortfolioData } from '../hooks/usePortfolioData';
import { getTopXAndOthers, CHART_COLORS } from '../utils/chartHelpers';
import LoadingSpinner from '../components/ui/LoadingSpinner';

// ── helpers ───────────────────────────────────────────────────────────────────

const getCostBasis = (p: Portfolio) =>
  [...(p.entries ?? []), ...(p.asset_entries ?? [])].reduce(
    (s, e) => s + ((e.purchase_price || 0) * (e.shares || 0)),
    0,
  );

const getEtfValue = (p: Portfolio) =>
  (p.entries ?? []).reduce((s, e) => s + (e.current_value || 0), 0);

const getDirectValue = (p: Portfolio) =>
  (p.asset_entries ?? []).reduce((s, e) => s + (e.current_value || 0), 0);

const gainColor = (v: number) => (v >= 0 ? '#16a34a' : '#dc2626');

// ── Sparkline (Recharts AreaChart) ───────────────────────────────────────────

interface SparklineProps {
  data: { date: string; value: number }[];
  color: string;
  height?: number;
}

const Sparkline: React.FC<SparklineProps> = ({ data, color, height = 60 }) => {
  const { formatAmount, formatAmountCompact } = useCurrency();
  const { isPrivateMode } = usePrivacy();
  if (!data || data.length < 2) return <div style={{ height }} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
        <CartesianGrid vertical={false} stroke="#f4f4f5" strokeDasharray="0" />
        <XAxis dataKey="date" hide />
        <YAxis
          domain={['auto', 'auto']}
          orientation="right"
          width={56}
          tickCount={4}
          tickFormatter={v => formatAmountCompact(v, isPrivateMode)}
          tick={{ fontSize: 10, fill: '#a1a1aa' }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const point = payload[0].payload as { date: string; value: number };
            return (
              <div style={{ background: 'white', border: '1px solid #f4f4f5', borderRadius: 6, padding: '5px 9px', fontSize: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <div style={{ color: '#a1a1aa', marginBottom: 2 }}>{point.date}</div>
                <div style={{ fontWeight: 600, color: '#000537' }}>
                  {formatAmount(point.value, isPrivateMode, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
              </div>
            );
          }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={1.5}
          fill="none"
          dot={false}
          activeDot={{ r: 3, fill: color, strokeWidth: 0 }}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const transformBreakdown = (breakdown: PortfolioHoldingsBreakdown | undefined) => {
  if (!breakdown) return { industryData: [], countryData: [] };
  const toArr = (obj: Record<string, number>) =>
    Object.entries(obj).map(([name, value]) => ({ name, value: Number(value) }));
  return {
    industryData: getTopXAndOthers(toArr(breakdown.industries ?? {})),
    countryData:  getTopXAndOthers(toArr(breakdown.countries ?? {})),
  };
};

// ── PerfRow — shows only all-time gain per category (period returns live in hero) ──

interface PerfRowProps {
  gain: number | null;
  gainPct: number | null;
  isPrivateMode: boolean;
}

const PerfRow: React.FC<PerfRowProps> = ({ gain, gainPct, isPrivateMode }) => {
  const { formatAmount } = useCurrency();
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f4f4f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div className="section-label">All-time</div>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: gainPct !== null ? gainColor(gainPct) : '#a1a1aa' }}>
        {gainPct !== null ? `${gainPct >= 0 ? '+' : ''}${gainPct.toFixed(1)}%` : '—'}
        {gain !== null && gainPct !== null && (
          <span style={{ fontWeight: 400, color: '#a1a1aa', fontSize: '0.72rem', marginLeft: 4 }}>
            ({gain >= 0 ? '+' : ''}{formatAmount(gain, isPrivateMode, { minimumFractionDigits: 0, maximumFractionDigits: 0 })})
          </span>
        )}
      </span>
    </div>
  );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const { user } = useAuth();
  const { isPrivateMode } = usePrivacy();
  const { formatAmount } = useCurrency();
  // ── Current user (for active_portfolio_id) ──────────────────────────────────
  const { data: currentUser, refetch: refetchCurrentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn:  () => authService.getCurrentUser(),
    enabled:  !!user,
    staleTime: 30_000,
  });
  const activePortfolioId: number | null = currentUser?.active_portfolio_id ?? null;

  // ── All portfolios ───────────────────────────────────────────────────────────
  const { data: portfolios = [], isLoading } = useQuery({
    queryKey: ['portfolios'],
    queryFn:  () => portfolioService.getPortfolios(),
  });

  const activePortfolio = useMemo(
    () => portfolios.find(p => p.id === activePortfolioId) ?? null,
    [portfolios, activePortfolioId],
  );

  // ── Set-active mutation ──────────────────────────────────────────────────────
  const setActiveMutation = useMutation({
    mutationFn: (id: number | null) =>
      api.patch('/users/active_portfolio', { portfolio_id: id }),
    onSuccess: () => refetchCurrentUser(),
  });

  // ── Active portfolio 90d performance (sparkline + positions) ─────────────────
  const sparkFrom = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  }, []);

  const { data: activePerformance } = useQuery({
    queryKey: ['portfolio-sparkline', activePortfolioId, sparkFrom],
    queryFn:  () => portfolioService.getPortfolioPerformance(activePortfolioId!, sparkFrom),
    enabled:  !!activePortfolioId,
    staleTime: 120_000,
  });

  const activePortfolioSparkline = activePerformance?.series;

  // ── Period returns from sparkline series ─────────────────────────────────────
  const { return7d, return1mo } = useMemo(() => {
    const series = activePortfolioSparkline ?? [];
    if (series.length < 2) return { return7d: null, return1mo: null };
    const current = series[series.length - 1].value;
    const anchorDate = (daysAgo: number) => {
      const d = new Date(); d.setDate(d.getDate() - daysAgo);
      return d.toISOString().slice(0, 10);
    };
    const findAnchor = (target: string) => [...series].reverse().find(p => p.date <= target);
    const a7  = findAnchor(anchorDate(7));
    const a30 = findAnchor(anchorDate(30));
    return {
      return7d:  a7  && a7.value  > 0 ? ((current - a7.value)  / a7.value)  * 100 : null,
      return1mo: a30 && a30.value > 0 ? ((current - a30.value) / a30.value) * 100 : null,
    };
  }, [activePortfolioSparkline]);

  // ── Top performer / biggest drag by daily change ─────────────────────────────
  const { topMover, topDrag } = useMemo(() => {
    const valid = (activePerformance?.positions ?? [])
      .filter(p => p.day_change_pct !== null && p.day_change_pct !== undefined);
    if (valid.length === 0) return { topMover: null, topDrag: null };
    const sorted = [...valid].sort((a, b) => (b.day_change_pct ?? 0) - (a.day_change_pct ?? 0));
    return { topMover: sorted[0], topDrag: sorted[sorted.length - 1] };
  }, [activePerformance]);

  // ── Portfolio analysis queries ───────────────────────────────────────────────
  const { data: combinedBreakdown, isLoading: combinedLoading } = useQuery({
    queryKey: ['portfolio-holdings-breakdown', activePortfolioId],
    queryFn:  () => portfolioService.getPortfolioHoldingsBreakdown(activePortfolioId!),
    enabled:  !!activePortfolioId,
  });
  const { data: totalExposureData, isLoading: exposureLoading } = useQuery({
    queryKey: ['portfolio-total-exposure', activePortfolioId],
    queryFn:  () => portfolioService.getPortfolioTotalExposure(activePortfolioId!),
    enabled:  !!activePortfolioId,
  });

  // ── Portfolio data derived values ────────────────────────────────────────────
  const { totalEtfValue, totalAssetValue } = usePortfolioData(activePortfolio ?? undefined);

  const { industryData: combinedIndustry } = transformBreakdown(combinedBreakdown);

  // ── Active portfolio computed values ─────────────────────────────────────────
  const activeTotal   = activePortfolio?.total_value ?? 0;
  const activeCost    = activePortfolio ? getCostBasis(activePortfolio) : 0;
  const activeGain    = activeCost > 0 ? activeTotal - activeCost : null;
  const activeGainPct = activeCost > 0 ? ((activeTotal - activeCost) / activeCost) * 100 : null;

  const etfCostBasis    = useMemo(() => (activePortfolio?.entries ?? []).reduce((s, e) => s + ((e.purchase_price || 0) * (e.shares || 0)), 0), [activePortfolio]);
  const directCostBasis = useMemo(() => (activePortfolio?.asset_entries ?? []).reduce((s, e) => s + ((e.purchase_price || 0) * (e.shares || 0)), 0), [activePortfolio]);
  const etfGain         = etfCostBasis > 0 ? totalEtfValue - etfCostBasis : null;
  const etfGainPct      = etfCostBasis > 0 ? ((totalEtfValue - etfCostBasis) / etfCostBasis) * 100 : null;
  const directGain      = directCostBasis > 0 ? totalAssetValue - directCostBasis : null;
  const directGainPct   = directCostBasis > 0 ? ((totalAssetValue - directCostBasis) / directCostBasis) * 100 : null;
  const cashValue       = Math.max(0, activeTotal - totalEtfValue - totalAssetValue);

  // ── Computed alerts (from active portfolio look-through) ─────────────────────
  const computedAlerts = useMemo(() => {
    const alerts: Array<{ severity: 'high' | 'medium' | 'low'; title: string; detail: string }> = [];
    if (!activePortfolio || activeTotal <= 0) return alerts;
    (totalExposureData?.securities ?? []).forEach(s => {
      const pct = (s.total_value / activeTotal) * 100;
      if (pct >= 8) {
        alerts.push({
          severity: pct >= 10 ? 'high' : 'medium',
          title: `${s.identifier} at ${pct.toFixed(1)}% of portfolio`,
          detail: 'Combined direct + look-through. Threshold: 8%.',
        });
      }
    });
    const equityPct = activeTotal > 0 ? ((totalEtfValue + totalAssetValue) / activeTotal) * 100 : 0;
    if (equityPct > 90) {
      alerts.push({
        severity: 'low',
        title: `Equities at ${equityPct.toFixed(0)}% of portfolio`,
        detail: 'Consider whether this matches your target allocation.',
      });
    }
    return alerts.slice(0, 3);
  }, [activePortfolio, activeTotal, totalExposureData, totalEtfValue, totalAssetValue]);

  // ── Loading / error ───────────────────────────────────────────────────────────
  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading dashboard…" />;
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-3 px-0">

      {/* ── Active portfolio hero ──────────────────────────────────────────── */}
      <div className="card card-forest mb-4">
        <div className="card-body p-3 p-md-4">

          {/* Portfolio switcher */}
          {portfolios.length > 0 && (
            <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
              <span className="section-label me-1">Portfolio</span>
              {activePortfolio && (
                <Link to={`/portfolios/${activePortfolio.id}?tab=performance`} style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#a1a1aa', textDecoration: 'none' }}>
                  View portfolio →
                </Link>
              )}
            </div>
          )}

          {activePortfolio ? (
            <>
              {/* Top row: value + sparkline side by side */}
              <div className="d-flex flex-column flex-md-row gap-4 align-items-md-stretch mb-3">
                {/* Left: value + performance */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div className="fw-semibold" style={{ fontSize: '2rem', color: '#000537', letterSpacing: '-0.025em', lineHeight: 1, marginBottom: 8 }}>
                    {formatAmount(activeTotal, isPrivateMode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>

                  {/* Period return chips */}
                  <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
                    {return7d !== null && (
                      <div>
                        <div className="section-label" style={{ marginBottom: 2 }}>7 day</div>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: gainColor(return7d) }}>
                          {return7d >= 0 ? '+' : ''}{return7d.toFixed(2)}%
                        </span>
                      </div>
                    )}
                    {return1mo !== null && (
                      <>
                        <div style={{ width: 1, height: 28, background: '#f4f4f5' }} />
                        <div>
                          <div className="section-label" style={{ marginBottom: 2 }}>1 month</div>
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: gainColor(return1mo) }}>
                            {return1mo >= 0 ? '+' : ''}{return1mo.toFixed(2)}%
                          </span>
                        </div>
                      </>
                    )}
                    {activeGainPct !== null && (
                      <>
                        <div style={{ width: 1, height: 28, background: '#f4f4f5' }} />
                        <div>
                          <div className="section-label" style={{ marginBottom: 2 }}>All-time</div>
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: gainColor(activeGain ?? 0) }}>
                            {(activeGain ?? 0) >= 0 ? '+' : ''}{activeGainPct.toFixed(2)}%
                            {activeGain !== null && (
                              <span style={{ fontWeight: 400, color: '#a1a1aa', fontSize: '0.8rem', marginLeft: 4 }}>
                                ({(activeGain ?? 0) >= 0 ? '+' : ''}{formatAmount(activeGain, isPrivateMode, { minimumFractionDigits: 0, maximumFractionDigits: 0 })})
                              </span>
                            )}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                </div>

                {/* Right: sparkline */}
                <div style={{ flex: '0 0 840px', minWidth: 0 }}>
                  <Sparkline data={activePortfolioSparkline ?? []} color="#000537" height={110} />
                  <div style={{ fontSize: '0.62rem', color: '#a1a1aa', marginTop: 3 }}>
                    90d
                  </div>
                </div>
              </div>

              {/* Full-width allocation bar */}
              {activeTotal > 0 && (
                <div style={{ borderTop: '1px solid #f4f4f5', paddingTop: 14 }}>
                  <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{ width: `${(totalEtfValue / activeTotal) * 100}%`, background: '#1a2666' }} title={`ETF: ${((totalEtfValue / activeTotal) * 100).toFixed(1)}%`} />
                    <div style={{ width: `${(totalAssetValue / activeTotal) * 100}%`, background: '#9faad4' }} title={`Direct: ${((totalAssetValue / activeTotal) * 100).toFixed(1)}%`} />
                    {cashValue > 0 && (
                      <div style={{ flex: 1, background: '#d4d4d8' }} title={`Cash: ${((cashValue / activeTotal) * 100).toFixed(1)}%`} />
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#1a2666', display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ color: '#52525b' }}>ETF</span>
                      <span style={{ color: '#a1a1aa', fontWeight: 600 }}>{((totalEtfValue / activeTotal) * 100).toFixed(1)}%</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#6b7abf', display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ color: '#52525b' }}>Direct</span>
                      <span style={{ color: '#a1a1aa', fontWeight: 600 }}>{((totalAssetValue / activeTotal) * 100).toFixed(1)}%</span>
                    </div>
                    {cashValue > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem' }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: '#d4d4d8', display: 'inline-block', flexShrink: 0 }} />
                        <span style={{ color: '#52525b' }}>Cash</span>
                        <span style={{ color: '#a1a1aa', fontWeight: 600 }}>{((cashValue / activeTotal) * 100).toFixed(1)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#a1a1aa', fontSize: '0.875rem' }}>
              {portfolios.length > 0 ? 'Select a portfolio above to view its dashboard' : (
                <Link to="/portfolios" className="btn btn-primary btn-sm">Create your first portfolio</Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── ETF / Direct / Cash breakdown + pulse tiles ────────────────────── */}
      {activePortfolio && (
        <div className="row g-3 mb-4">

          {/* ETF holdings */}
          <div className="col-12 col-md-2">
            <div className="card card-forest h-100">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span className="section-label">ETF holdings</span>
                  <span style={{ padding: '1px 7px', borderRadius: 999, background: '#f4f4f5', fontSize: '0.68rem', color: '#52525b', fontWeight: 600 }}>
                    {activeTotal > 0 ? ((totalEtfValue / activeTotal) * 100).toFixed(1) : '0.0'}%
                  </span>
                </div>
                <div className="fw-semibold" style={{ fontSize: '1.4rem', color: '#000537', letterSpacing: '-0.02em', lineHeight: 1 }}>
                  {formatAmount(totalEtfValue, isPrivateMode, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
                <PerfRow gain={etfGain} gainPct={etfGainPct} isPrivateMode={isPrivateMode} />

              </div>
            </div>
          </div>

          {/* Direct holdings */}
          <div className="col-12 col-md-2">
            <div className="card card-forest h-100">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span className="section-label">Direct holdings</span>
                  <span style={{ padding: '1px 7px', borderRadius: 999, background: '#f4f4f5', fontSize: '0.68rem', color: '#52525b', fontWeight: 600 }}>
                    {activeTotal > 0 ? ((totalAssetValue / activeTotal) * 100).toFixed(1) : '0.0'}%
                  </span>
                </div>
                <div className="fw-semibold" style={{ fontSize: '1.4rem', color: '#000537', letterSpacing: '-0.02em', lineHeight: 1 }}>
                  {formatAmount(totalAssetValue, isPrivateMode, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
                <PerfRow gain={directGain} gainPct={directGainPct} isPrivateMode={isPrivateMode} />
 
              </div>
            </div>
          </div>

          {/* Cash & other */}
          <div className="col-12 col-md-2">
            <div className="card card-forest h-100">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span className="section-label">Cash &amp; other</span>
                  <span style={{ padding: '1px 7px', borderRadius: 999, background: '#f4f4f5', fontSize: '0.68rem', color: '#a1a1aa', fontWeight: 700 }}>
                    {activeTotal > 0 ? ((cashValue / activeTotal) * 100).toFixed(1) : '0.0'}%
                  </span>
                </div>
                <div className="fw-semibold" style={{ fontSize: '1.4rem', color: '#000537', letterSpacing: '-0.02em', lineHeight: 1 }}>
                  {formatAmount(cashValue, isPrivateMode, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
          </div>

          {/* Top performer */}
          <div className="col-12 col-md-6">
            <div className="card card-forest h-100">
              <div className="card-body p-3">
                <div className="row g-3 mb-4 justify-content-between">
                  <div className="col-12 col-md-5" >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="section-label">Top performer</span>
                  <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>▲</span>
                </div>
                {topMover ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-semibold" style={{ fontSize: '1.05rem', color: '#000537', letterSpacing: '-0.01em' }}>{topMover.ticker}</div>
                        <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{topMover.name}</div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#16a34a', flexShrink: 0 }}>
                        +{(topMover.day_change_pct ?? 0).toFixed(2)}%
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#a1a1aa', fontSize: '0.8rem' }}>No daily data yet</div>
                )}
              </div>
                <div className="col-md-1" >
              <div style={{ width: '1px', background: '#f4f4f5', height:'100%' }}></div>
                </div>

          {/* Biggest drag */}
                <div className="col-12 col-md-5">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="section-label">Biggest drag</span>
                  <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>▼</span>
                </div>
                {topDrag ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-startt', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-semibold" style={{ fontSize: '1.05rem', color: '#000537', letterSpacing: '-0.01em' }}>{topDrag.ticker}</div>
                        <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{topDrag.name}</div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#dc2626', flexShrink: 0 }}>
                        {(topDrag.day_change_pct ?? 0).toFixed(2)}%
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#a1a1aa', fontSize: '0.8rem' }}>No daily data yet</div>
                )}
              </div>
              </div>
              </div>
            </div>
          </div>

          {/* Needs attention */}
          <div className="col-12 col-md-12">
            <div className="card card-forest h-100">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="section-label">Needs attention</span>
                  {computedAlerts[0] && (
                    <span style={{ fontSize: '0.72rem', color: '#a1a1aa' }}>{computedAlerts.length} alert{computedAlerts.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
                {computedAlerts[0] ? (
                  <>
                    <div className="fw-semibold" style={{ fontSize: '0.875rem', color: '#000537', lineHeight: 1.3, marginBottom: 6 }}>
                      {computedAlerts[0].title}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#71717a', lineHeight: 1.5 }}>
                      {computedAlerts[0].detail}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="fw-semibold" style={{ fontSize: '0.875rem', color: '#000537', marginBottom: 4 }}>All clear</div>
                    <div style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>No concentration or allocation alerts</div>
                  </>
                )}
              </div>
            </div>
          </div>

        </div>
      )}

      {/* ── Portfolio insights ────────────────────────────────────────────── */}
      {activePortfolio && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="fw-semibold" style={{ color: '#000537', fontSize: '0.95rem' }}>
              Portfolio insights
            </span>
            {(combinedLoading || exposureLoading) && (
              <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>Loading…</span>
            )}
          </div>

          <div className="row g-3">
            {/* Sector exposure stacked bar */}
            <div className="col-12 col-lg-7">
              <div className="card card-forest h-100">
                <div className="card-body p-3 p-md-4">
                  <div className="mb-3">
                    <h5 className="card-title mb-0 fw-semibold" style={{ color: '#000537', fontSize: '0.95rem' }}>
                      Sector Exposure
                    </h5>
                    <div style={{ fontSize: '0.72rem', color: '#a1a1aa', marginTop: 2 }}>Look-through across all holdings</div>
                  </div>
                  {combinedIndustry.length > 0 ? (
                    <>
                      {/* Stacked bar */}
                      <div style={{ display: 'flex', height: 28, borderRadius: 5, overflow: 'hidden', background: '#f4f4f5', marginBottom: 16 }}>
                        {combinedIndustry.slice(0, 8).map((s, i) => (
                          <div
                            key={s.name}
                            style={{ width: `${s.value * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length] }}
                            title={`${s.name}: ${(s.value * 100).toFixed(1)}%`}
                          />
                        ))}
                      </div>
                      {/* Legend — vertical list */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {combinedIndustry.slice(0, 8).map((s, i) => (
                          <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem' }}>
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0 }} />
                            <span style={{ flex: 1, color: '#52525b' }}>{s.name}</span>
                            <span style={{ color: '#a1a1aa', fontWeight: 500 }}>{(s.value * 100).toFixed(1)}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>
                      {combinedLoading ? 'Loading sector data…' : 'No sector data available'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top 5 securities look-through */}
            <div className="col-12 col-lg-5">
              <div className="card card-forest h-100">
                <div className="card-body p-3 p-md-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0 fw-semibold" style={{ color: '#000537', fontSize: '0.95rem' }}>
                      Top 5 Securities
                    </h5>
                    <span style={{ fontSize: '0.65rem', padding: '2px 7px', borderRadius: 999, background: '#f4f4f5', color: '#52525b', fontWeight: 600 }}>
                      Look-through
                    </span>
                  </div>
                  {(totalExposureData?.securities?.length ?? 0) > 0 ? (
                    <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                      <thead>
                        <tr>
                          <th className="border-0 ps-0 pb-2" style={{ color: '#a1a1aa', fontWeight: 500, fontSize: '0.72rem', letterSpacing: '0.03em', textTransform: 'uppercase' }}>Security</th>
                          <th className="border-0 pb-2 text-end" style={{ color: '#a1a1aa', fontWeight: 500, fontSize: '0.72rem', letterSpacing: '0.03em', textTransform: 'uppercase' }}>Value</th>
                          <th className="border-0 pb-2 text-end" style={{ color: '#a1a1aa', fontWeight: 500, fontSize: '0.72rem', letterSpacing: '0.03em', textTransform: 'uppercase' }}>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(totalExposureData?.securities ?? []).slice(0, 5).map((s, i) => {
                          const share = activeTotal > 0 ? (s.total_value / activeTotal) * 100 : 0;
                          return (
                            <tr key={s.identifier} style={{ borderColor: '#f4f4f5' }}>
                              <td className="border-0 ps-0 py-2">
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 1, background: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0 }} />
                                  <div>
                                    <div className="fw-semibold" style={{ color: '#18181b', lineHeight: 1.2 }}>{s.identifier}</div>
                                    <div style={{ color: '#a1a1aa', fontSize: '0.7rem' }}>{s.name}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="border-0 py-2 text-end fw-semibold" style={{ color: '#000537' }}>
                                {formatAmount(s.total_value, isPrivateMode, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </td>
                              <td className="border-0 py-2 text-end" style={{ color: '#71717a' }}>
                                {share.toFixed(1)}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>
                      {exposureLoading ? 'Loading securities data…' : 'No securities data available'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Alerts & insights ─────────────────────────────────────────────── */}
      {computedAlerts.length > 0 && (
        <div className="card card-forest mb-4">
          <div className="card-body p-3 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="fw-semibold" style={{ color: '#000537', fontSize: '0.95rem' }}>
                Alerts &amp; insights
              </span>
              <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>
                {computedAlerts.length} active
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {computedAlerts.map((a, i) => {
                const sevColor = a.severity === 'high' ? '#dc2626' : '#a1a1aa';
                return (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: i < computedAlerts.length - 1 ? '1px solid #f4f4f5' : 'none', alignItems: 'flex-start' }}>
                    <div style={{ width: 3, alignSelf: 'stretch', background: sevColor, borderRadius: 2, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#000537', marginBottom: 3 }}>{a.title}</div>
                      <div style={{ fontSize: '0.75rem', color: '#71717a', lineHeight: 1.4 }}>{a.detail}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};



export default Dashboard;
